import { logger } from '../utils/logger';
// Sound Effects Service
// Manage audio feedback for user interactions

class SoundEffectsService {
  constructor() {
    this.enabled = true
    this.volume = 0.5 // 0.0 to 1.0
    this.sounds = {}
    this.preloadSounds()
  }

  // Preload all sound files
  preloadSounds() {
    const soundFiles = {
      correct: '/sounds/correct.mp3',
      incorrect: '/sounds/incorrect.mp3',
      pronunciationGood: '/sounds/pronunciation-good.mp3',
      pronunciationBad: '/sounds/pronunciation-bad.mp3',
      click: '/sounds/click.mp3',
      submit: '/sounds/submit.mp3',
      notification: '/sounds/collect-5930.mp3'
    }

    Object.entries(soundFiles).forEach(([key, path]) => {
      const audio = new Audio()
      audio.src = path
      audio.volume = this.volume
      audio.preload = 'auto'
      this.sounds[key] = audio
      
      // Handle load error gracefully
      audio.addEventListener('error', () => {
        logger.warn(`Failed to load sound: ${path}`)
        this.sounds[key] = null
      })
    })
  }

  // Play sound by key
  play(soundKey) {
    if (!this.enabled) return

    const audio = this.sounds[soundKey]
    if (!audio) {
      logger.warn(`Sound not found: ${soundKey}`)
      return
    }

    // Clone audio to allow overlapping plays
    const clone = audio.cloneNode()
    clone.volume = this.volume

    return clone.play().catch(err => {
      // Ignore play errors (e.g., user hasn't interacted yet)
      logger.debug('Audio play prevented:', err.message)
    })
  }

  /**
   * Play a notification sound independently of the gameplay toggle/volume.
   * Returns a promise resolving to the playback outcome; a browser rejection
   * (e.g. NotAllowedError before user interaction) resolves to false rather than
   * throwing, so inbox delivery is never blocked.
   */
  playWithOptions(soundKey, { enabled = true, volume = 0.35 } = {}) {
    if (!enabled) return Promise.resolve(false)
    const audioPath = this.sounds[soundKey]
      ? this.sounds[soundKey].src
      : soundKey
    try {
      const clone = new Audio(audioPath)
      clone.volume = Math.max(0, Math.min(1, volume))
      return clone.play().then(() => true).catch((err) => {
        logger.debug('Notification audio play prevented:', err?.message)
        return false
      })
    } catch (err) {
      logger.debug('Notification audio failed:', err?.message)
      return Promise.resolve(false)
    }
  }

  // Specific sound methods
  playCorrect() {
    this.play('correct')
  }

  playIncorrect() {
    this.play('incorrect')
  }

  playPronunciationGood() {
    this.play('pronunciationGood')
  }

  playPronunciationBad() {
    this.play('pronunciationBad')
  }

  playClick() {
    this.play('click')
  }

  playSubmit() {
    this.play('submit')
  }

  // Settings
  setEnabled(enabled) {
    this.enabled = enabled
    localStorage.setItem('sound_effects_enabled', enabled)
  }

  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume))
    Object.values(this.sounds).forEach(audio => {
      if (audio) audio.volume = this.volume
    })
    localStorage.setItem('sound_effects_volume', this.volume)
  }

  getEnabled() {
    const stored = localStorage.getItem('sound_effects_enabled')
    return stored !== null ? stored === 'true' : true
  }

  getVolume() {
    const stored = localStorage.getItem('sound_effects_volume')
    return stored !== null ? parseFloat(stored) : 0.5
  }

  // Initialize from localStorage
  loadSettings() {
    this.enabled = this.getEnabled()
    this.volume = this.getVolume()
  }
}

// Singleton instance
export const soundEffectsService = new SoundEffectsService()
soundEffectsService.loadSettings()

export default soundEffectsService

